import React from 'react';
import { Drawer } from '@mui/material';
import { ImportFromRecipeLibraryModal, type RecipeLibraryItem } from '@/components/shared/ImportFromRecipeLibraryModal';
import { SaveAsTemplateModal, type SaveAsTemplatePayload } from '@/components/shared/SaveAsTemplateModal';
import RecipeDetailsModal from '@/components/meal-planning/RecipeDetailsModal';
import {
  ManualMealPlanAnalysisDashboard,
  type DayAnalysisSnapshot,
} from '@/components/meal-planning/manual/ManualMealPlanAnalysisDashboard';
import ManualPlanConfirmDialog from '@/components/meal-planning/manual/ManualPlanConfirmDialog';
import AddMealPlanItemPopover from '@/components/meal-planning/manual/AddMealPlanItemPopover';
import EditMealSlotDialog from './EditMealSlotDialog';
import type { AddItemSearchFilters, FoodSearchResult, SelectedRecipeDetailsContext } from './nutritionalAnalysisTypes';

type NutritionalAnalysisOverlaysProps = {
  isAddItemModalOpen: boolean;
  addItemAnchorEl: HTMLElement | null;
  closeAddItemModal: () => void;
  addItemMealName: string | null;
  isAddItemSearching: boolean;
  isAddItemAdding?: boolean;
  addItemAddingOption?: string | null;
  addItemSearchValue: string;
  setAddItemSearchValue: (value: string) => void;
  addItemSearchInputRef: React.MutableRefObject<HTMLInputElement | null>;
  addItemOptionNames: string[];
  addItemSearchResults: FoodSearchResult[];
  addItemSearchFilters: AddItemSearchFilters;
  addItemCurrentAllergies: string[];
  addItemCurrentIntolerances: string[];
  handleAddItemFromModal: (itemName: string) => Promise<void>;
  onCreateCustomRecipe?: () => void;
  isImportRecipesOpen: boolean;
  closeImportRecipesModal: () => void;
  importRecipesMealName: string | null;
  hiddenImportedRecipeIds: string[];
  excludedAllergies: string[];
  handleImportRecipes: (selectedRecipes: RecipeLibraryItem[]) => Promise<void>;
  editMealDialogOpen: boolean;
  mealSlotDialogMode: 'add' | 'edit';
  isSavingMealSlot: boolean;
  editMealName: string;
  editMealType: string;
  canEditMealType: boolean;
  mealTypeOptions: Array<{ value: string; label: string }>;
  editMealTime: string;
  editMealTargetKcal: string;
  setEditMealName: (value: string) => void;
  setEditMealType: (value: string) => void;
  setEditMealTime: (value: string) => void;
  setEditMealTargetKcal: (value: string) => void;
  closeEditMealSlotDialog: () => void;
  handleSaveMealSlotEdit: () => Promise<void>;
  selectedRecipeDetails: SelectedRecipeDetailsContext | null;
  setSelectedRecipeDetails: React.Dispatch<React.SetStateAction<SelectedRecipeDetailsContext | null>>;
  resolvedPlanId?: string;
  planStatus?: string;
  selectedDayNumber: number;
  resolveMealApiName: (mealName: string) => string;
  loadDayData: () => Promise<void>;
  isSaveTemplateOpen: boolean;
  closeSaveTemplateModal: () => void;
  saveTemplateKind: 'meal_plan' | 'meal_slot';
  dayLabel: string;
  mealCount: number;
  totalCalories: number;
  saveTemplateSummaryDetail: string;
  handleSaveTemplate: (payload: SaveAsTemplatePayload) => Promise<void>;
  deleteMealSlotConfirmOpen: boolean;
  setDeleteMealSlotConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  pendingDeleteMealSlotName: string | null;
  mealSlotDeletingName: string | null;
  handleDeleteMealSlotConfirmSubmit: () => Promise<void>;
  deleteTemplateConfirmOpen: boolean;
  setDeleteTemplateConfirmOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isDeletingTemplate: boolean;
  handleDeleteTemplateConfirmSubmit: () => Promise<void>;
  manualAnalysisDrawerOpen: boolean;
  setManualAnalysisDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  manualAnalysisSnapshot: DayAnalysisSnapshot;
  targetDailyCalories: number;
  onViewDetailedAnalysis?: () => void;
};

const NutritionalAnalysisOverlays: React.FC<NutritionalAnalysisOverlaysProps> = ({
  isAddItemModalOpen,
  addItemAnchorEl,
  closeAddItemModal,
  addItemMealName,
  isAddItemSearching,
  isAddItemAdding = false,
  addItemAddingOption = null,
  addItemSearchValue,
  setAddItemSearchValue,
  addItemSearchInputRef,
  addItemOptionNames,
  addItemSearchResults,
  addItemSearchFilters,
  addItemCurrentAllergies,
  addItemCurrentIntolerances,
  handleAddItemFromModal,
  onCreateCustomRecipe,
  isImportRecipesOpen,
  closeImportRecipesModal,
  importRecipesMealName,
  hiddenImportedRecipeIds,
  excludedAllergies,
  handleImportRecipes,
  editMealDialogOpen,
  mealSlotDialogMode,
  isSavingMealSlot,
  editMealName,
  editMealType,
  canEditMealType,
  mealTypeOptions,
  editMealTime,
  editMealTargetKcal,
  setEditMealName,
  setEditMealType,
  setEditMealTime,
  setEditMealTargetKcal,
  closeEditMealSlotDialog,
  handleSaveMealSlotEdit,
  selectedRecipeDetails,
  setSelectedRecipeDetails,
  resolvedPlanId,
  planStatus,
  selectedDayNumber,
  resolveMealApiName,
  loadDayData,
  isSaveTemplateOpen,
  closeSaveTemplateModal,
  saveTemplateKind,
  dayLabel,
  mealCount,
  totalCalories,
  saveTemplateSummaryDetail,
  handleSaveTemplate,
  deleteMealSlotConfirmOpen,
  setDeleteMealSlotConfirmOpen,
  pendingDeleteMealSlotName,
  mealSlotDeletingName,
  handleDeleteMealSlotConfirmSubmit,
  deleteTemplateConfirmOpen,
  setDeleteTemplateConfirmOpen,
  isDeletingTemplate,
  handleDeleteTemplateConfirmSubmit,
  manualAnalysisDrawerOpen,
  setManualAnalysisDrawerOpen,
  manualAnalysisSnapshot,
  targetDailyCalories,
  onViewDetailedAnalysis,
}) => (
  <>
    <AddMealPlanItemPopover
      open={isAddItemModalOpen}
      anchorEl={addItemAnchorEl}
      onClose={closeAddItemModal}
      mealName={addItemMealName}
      isSearching={isAddItemSearching}
      isAdding={isAddItemAdding}
      addingOption={addItemAddingOption}
      searchValue={addItemSearchValue}
      onSearchChange={setAddItemSearchValue}
      searchInputRef={addItemSearchInputRef}
      options={addItemOptionNames}
      searchResults={addItemSearchResults}
      searchFilterAllergies={addItemSearchFilters.allergies}
      currentAllergies={addItemCurrentAllergies}
      currentIntolerances={addItemCurrentIntolerances}
      onAddOption={handleAddItemFromModal}
      onCreateCustomRecipe={onCreateCustomRecipe}
    />

    <ImportFromRecipeLibraryModal
      open={isImportRecipesOpen}
      onClose={closeImportRecipesModal}
      mealName={importRecipesMealName}
      hiddenRecipeIds={hiddenImportedRecipeIds}
      excludedAllergies={excludedAllergies}
      onImport={handleImportRecipes}
    />

    <EditMealSlotDialog
      open={editMealDialogOpen}
      mode={mealSlotDialogMode}
      isSaving={isSavingMealSlot}
      mealName={editMealName}
      mealType={editMealType}
      canEditMealType={canEditMealType}
      mealTypeOptions={mealTypeOptions}
      mealTime={editMealTime}
      targetKcal={editMealTargetKcal}
      onMealNameChange={setEditMealName}
      onMealTypeChange={setEditMealType}
      onMealTimeChange={setEditMealTime}
      onTargetKcalChange={setEditMealTargetKcal}
      onClose={closeEditMealSlotDialog}
      onSave={() => void handleSaveMealSlotEdit()}
    />

    {selectedRecipeDetails ? (
      <RecipeDetailsModal
        recipeId={selectedRecipeDetails.recipeId}
        recipeName={selectedRecipeDetails.recipeName}
        open
        onOpenChange={(open) => {
          if (!open) setSelectedRecipeDetails(null);
        }}
        draftId={planStatus === 'draft' ? resolvedPlanId : undefined}
        mealPlanId={planStatus !== 'draft' ? resolvedPlanId : undefined}
        itemId={selectedRecipeDetails.itemId}
        day={selectedDayNumber}
        mealName={resolveMealApiName(selectedRecipeDetails.mealName)}
        mealOrder={selectedRecipeDetails.mealOrder}
        source={selectedRecipeDetails.source ?? 'spoonacular'}
        onClose={() => {
          setSelectedRecipeDetails(null);
          void loadDayData();
        }}
        onRefetchDraft={loadDayData}
        restrictIngredientEditToQuantity={false}
      />
    ) : null}

    <SaveAsTemplateModal
      open={isSaveTemplateOpen}
      onClose={closeSaveTemplateModal}
      dayLabel={dayLabel}
      mealCount={mealCount}
      totalKcal={totalCalories}
      templateKind={saveTemplateKind}
      summaryDetail={saveTemplateSummaryDetail}
      onSave={handleSaveTemplate}
    />
    <ManualPlanConfirmDialog
      open={deleteMealSlotConfirmOpen}
      onOpenChange={setDeleteMealSlotConfirmOpen}
      title="Delete meal slot?"
      description={`Are you sure you want to delete the "${pendingDeleteMealSlotName || ''}" meal slot? This will remove all food in this slot.`}
      confirmText="Delete"
      loading={Boolean(mealSlotDeletingName)}
      onConfirm={handleDeleteMealSlotConfirmSubmit}
    />
    <ManualPlanConfirmDialog
      open={deleteTemplateConfirmOpen}
      onOpenChange={setDeleteTemplateConfirmOpen}
      title="Delete template?"
      description="This will permanently delete this template. This action cannot be undone."
      confirmText="Delete"
      loading={isDeletingTemplate}
      confirmButtonClassName="bg-[#FF6262] text-white hover:bg-[#FF6262] focus:ring-[#FF6262]"
      onConfirm={handleDeleteTemplateConfirmSubmit}
    />

    <Drawer
      anchor="right"
      open={manualAnalysisDrawerOpen}
      onClose={() => setManualAnalysisDrawerOpen(false)}
      ModalProps={{ keepMounted: true }}
      BackdropProps={{
        sx: {
          bgcolor: 'rgba(6, 16, 32, 0.56)',
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 560 },
          borderLeft: '1px solid #e5e7eb',
          bgcolor: '#f3f4f6',
          boxShadow: '-28px 0 44px rgba(15, 23, 42, 0.12)',
          // Avoid clipping Calories by Meal kcal / % columns inside ManualMealPlanAnalysisDashboard.
          overflowX: 'auto',
        },
      }}
    >
      <ManualMealPlanAnalysisDashboard
        selectedAnalysis={manualAnalysisSnapshot}
        targetDailyCalories={targetDailyCalories}
        onClose={() => setManualAnalysisDrawerOpen(false)}
        onViewDetailedAnalysis={onViewDetailedAnalysis}
      />
    </Drawer>
  </>
);

export default NutritionalAnalysisOverlays;
